import React from 'react';
import Konva from 'konva';
import { ScaleBarProps } from 'business/models/CustomModels';
import { changeDrawingIcon } from 'components/well/utils';
import { Rect, Text, Group, Line } from 'react-konva';

const SCALE_BAR_GRIP_WIDTH_PX = 10;
const SCALE_BAR_DRAG_CURSOR = 'move';
const SCALE_BAR_DEFAULT_CURSOR = 'default';

type ScaleBarMouseEvent = Konva.KonvaEventObject<MouseEvent>;
type ScaleBarDragEvent = Konva.KonvaEventObject<DragEvent>;

export default function ScaleBarRenderer(props: ScaleBarProps) {
  const x = props.x + SCALE_BAR_GRIP_WIDTH_PX / props.scale;
  const scaleBarPerToHeight = 0.25;
  const scaleBarStartingXPerToWidth = 0.1;
  const scaleBarLeftMargin = props.width * scaleBarStartingXPerToWidth;
  const scaleBarRectX = x + scaleBarLeftMargin;
  const scaleBarRectY =
    props.y + props.height / 2 - (props.height * scaleBarPerToHeight) / 2;
  const scaleBarWidth = props.scaleBarWidth;
  const scaleBarHeight = props.scaleBarHeight;
  // Total footprint of the bar, including the grip on its left
  const footprintWidth = props.width + SCALE_BAR_GRIP_WIDTH_PX / props.scale;

  const stopPropagation = (e: ScaleBarMouseEvent | ScaleBarDragEvent) => {
    e.cancelBubble = true;
  };

  // Keep the bar inside the canvas while it is dragged
  const onDragMove = (e: ScaleBarDragEvent) => {
    e.cancelBubble = true;
    if (!props.bounds) {
      return;
    }
    const group = e.target;
    const maxX = props.bounds.width - footprintWidth;
    const maxY = props.bounds.height - props.height;
    group.x(Math.min(Math.max(props.x + group.x(), 0), Math.max(maxX, 0)) - props.x);
    group.y(Math.min(Math.max(props.y + group.y(), 0), Math.max(maxY, 0)) - props.y);
  };

  // Hand the new position to the parent and reset the group's own offset
  const onDragEnd = (e: ScaleBarDragEvent) => {
    e.cancelBubble = true;
    const group = e.target;
    const newX = props.x + group.x();
    const newY = props.y + group.y();
    group.position({ x: 0, y: 0 });
    props.onPositionChange?.(newX, newY);
  };

  return (
    <Group
      id={`scalebarrenderer-${props.text}-group`}
      draggable={props.draggable}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onDragStart={stopPropagation}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onMouseEnter={(e: ScaleBarMouseEvent) =>
        props.draggable && changeDrawingIcon(e, SCALE_BAR_DRAG_CURSOR)
      }
      onMouseLeave={(e: ScaleBarMouseEvent) =>
        props.draggable && changeDrawingIcon(e, SCALE_BAR_DEFAULT_CURSOR)
      }
    >
      <Rect
        id={`scalebarrenderer-${props.text}-rect-1`}
        x={x}
        y={props.y}
        width={props.width}
        height={props.height}
        fill="#787a7c"
        opacity={0.6}
      />
      <Rect
        id={`scalebarrenderer-${props.text}-rect-2`}
        x={props.x}
        y={props.y}
        width={SCALE_BAR_GRIP_WIDTH_PX / props.scale}
        height={props.height}
        borderRadius={3}
        fill="#787a7c"
        opacity={0.8}
      />
      <Line
        id={`scalebarrenderer-${props.text}-line`}
        points={[
          props.x + 5 / props.scale,
          props.y,
          props.x + 5 / props.scale,
          props.y + props.height,
        ]}
        stroke={'black'}
        dash={[2 / props.scale, 2 / props.scale]}
        strokeWidth={2 / props.scale}
        opacity={0.8}
      />
      <Rect
        id={`scalebarrenderer-${props.text}-rect-3`}
        x={scaleBarRectX}
        y={scaleBarRectY - 3 / props.scale}
        width={3 / props.scale}
        height={scaleBarHeight + 6 / props.scale}
        borderRadius={3}
        fill="white"
        opacity={1}
      />
      <Rect
        id={`scalebarrenderer-${props.text}-rect-4`}
        x={scaleBarRectX}
        y={scaleBarRectY}
        width={scaleBarWidth}
        height={scaleBarHeight}
        fill="white"
        opacity={1}
      />
      <Rect
        id={`scalebarrenderer-${props.text}-rect-5`}
        x={scaleBarRectX}
        y={scaleBarRectY}
        width={scaleBarWidth}
        height={scaleBarHeight}
        fill="white"
        opacity={1}
      />
      <Text
        id={`scalebarrenderer-${props.text}-text`}
        x={x + scaleBarWidth + scaleBarLeftMargin * 2}
        y={props.y}
        height={props.height}
        fontSize={props.fontSize}
        verticalAlign="middle"
        align="center"
        textAnchor="middle"
        text={props.text}
        fill="white"
        opacity={0.8}
      />
      <Rect
        id={`scalebarrenderer-${props.text}-rect-6`}
        x={scaleBarRectX + scaleBarWidth - 3 / props.scale}
        y={scaleBarRectY - 3 / props.scale}
        width={3 / props.scale}
        height={scaleBarHeight + 6 / props.scale}
        borderRadius={3}
        fill="white"
        opacity={1}
      />
    </Group>
  );
}
